import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Download, 
  Shield, 
  ChevronRight, 
  FileText, 
  Check, 
  AlertTriangle,
  CreditCard,
  Car,
  Phone,
  Home,
  Briefcase,
  GraduationCap,
  Heart,
  Building,
  HelpCircle,
  Zap,
  DollarSign,
  ClipboardPaste,
  Sparkles,
  Copy,
  Loader2,
  ChevronDown,
  ChevronUp,
  Eye,
  X
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

// ============ FRAUD CATEGORIES - 11 FTC Categories ============
const FRAUD_CATEGORIES = [
  { value: "CREDIT_CARD", label: "Credit Card", icon: CreditCard },
  { value: "AUTO", label: "Auto", icon: Car },
  { value: "LOAN_LEASE", label: "Loan/Lease", icon: DollarSign },
  { value: "MOBILE", label: "Mobile/Phone", icon: Phone },
  { value: "UTILITY", label: "Utility", icon: Zap },
  { value: "RENTAL", label: "Rental", icon: Home },
  { value: "MEDICAL", label: "Medical", icon: Heart },
  { value: "STUDENT_LOAN", label: "Student Loan", icon: GraduationCap },
  { value: "REAL_ESTATE", label: "Real Estate", icon: Building },
  { value: "INSURANCE", label: "Insurance", icon: Shield },
  { value: "OTHER", label: "Other", icon: HelpCircle }
];

// ============ DISPUTE LETTER TYPES ============
const LETTER_TYPES = [
  { value: "REGULAR", label: "Regular Dispute Letter", description: "Standard dispute for fraudulent accounts (listed by category)", icon: FileText },
  { value: "VERIFIED", label: "Verified Accounts Letter", description: "Round 2/3 - Accounts previously verified but still disputing", icon: AlertTriangle },
  { value: "RE_ADDED", label: "Re-Added Accounts Letter", description: "Accounts that were removed but re-added to report", icon: AlertTriangle },
  { value: "STUDENT_LOAN", label: "Student Loans Letter", description: "Specialized letter for student loan disputes", icon: GraduationCap },
  { value: "CHILD_SUPPORT", label: "Child Support Letter", description: "Specialized letter for child support disputes", icon: Briefcase },
  { value: "LATE_PAYMENTS", label: "Late Payments Only Letter", description: "Dispute only late payment marks, not the account", icon: FileText }
];

// ============ LETTER TEMPLATES (6 unique variations for E-Oscar) ============
const LETTER_TEMPLATES = [
  { 
    id: 1, 
    name: "Aggressive Legal Demand", 
    description: "Opens with legal citations, demands 7-day compliance",
    category: "identity_theft",
    color: "red",
    recommended: "Round 1 disputes"
  },
  { 
    id: 2, 
    name: "Documentation-First", 
    description: "Leads with evidence, formal corporate tone",
    category: "identity_theft",
    color: "blue",
    recommended: "Strong evidence cases"
  },
  { 
    id: 3, 
    name: "Regulatory Escalation", 
    description: "CFPB enforcement focus, warns of complaints",
    category: "identity_theft",
    color: "purple",
    recommended: "Round 2/3 disputes"
  },
  { 
    id: 4, 
    name: "ID Theft + Metro 2", 
    description: "DUAL BASIS: Identity theft + Metro 2 violations as proof",
    category: "identity_theft_metro2",
    color: "orange",
    recommended: "When bureaus deny ID theft claims"
  },
  { 
    id: 5, 
    name: "ID Theft + CDIA", 
    description: "COMBINED: Identity theft + CDIA proving verification impossible",
    category: "identity_theft_metro2",
    color: "amber",
    recommended: "Maximum strength dispute"
  },
  { 
    id: 6, 
    name: "Consumer Rights", 
    description: "Softer tone, emphasizes consumer protections",
    category: "consumer_rights",
    color: "green",
    recommended: "Professional approach"
  }
];

// ============ SMART DOCUMENT RECOMMENDATIONS ============
const getDocumentRecommendations = (categories, letterTypes) => {
  const recommendations = {
    required: [
      { name: "Identity Theft Dispute Letter", reason: "Core document for all identity theft cases" },
      { name: "FTC Identity Theft Report", reason: "Required by FCRA 15 U.S.C. §1681c-2" },
      { name: "Government-Issued Photo ID", reason: "Proves identity for bureau verification" },
      { name: "Social Security Card (Copy)", reason: "Confirms SSN for dispute validation" }
    ],
    recommended: [],
    optional: []
  };

  // Category-specific recommendations
  const categoryDocs = {
    'CREDIT_CARD': { name: "Bank Statement Showing No Activity", reason: "Proves you never used the card" },
    'AUTO': { name: "DMV Records Request", reason: "Shows no vehicle registration in your name" },
    'STUDENT_LOAN': { name: "Enrollment Verification Letter", reason: "Proves you didn't attend the institution" },
    'MEDICAL': { name: "HIPAA Medical Records Request", reason: "Proves no treatment at facility" },
    'UTILITY': { name: "Utility Company Non-Customer Letter", reason: "Confirms no account existed" },
    'RENTAL': { name: "Lease/Residency Verification", reason: "Proves you never lived at address" },
    'REAL_ESTATE': { name: "Property Deed Search", reason: "Shows property not in your name" }
  };

  // Check selected categories
  const uniqueCategories = [...new Set(Object.values(categories))];
  uniqueCategories.forEach(cat => {
    if (categoryDocs[cat]) {
      recommendations.recommended.push(categoryDocs[cat]);
    }
  });

  // Letter type specific recommendations
  if (letterTypes.includes('VERIFIED') || letterTypes.includes('RE_ADDED')) {
    recommendations.recommended.push({
      name: "Previous Deletion Confirmation",
      reason: "Proves account was previously removed"
    });
  }

  // Metro 2 / Technical recommendations
  recommendations.optional.push({
    name: "Identity Theft + Metro 2 Letter (Template #4 or #5)",
    reason: "Adds Metro 2 compliance violations as additional proof"
  });
  
  recommendations.optional.push({
    name: "Certified Mail Receipts",
    reason: "Creates paper trail for legal proceedings"
  });

  return recommendations;
};

const BUREAUS = ['EQUIFAX', 'EXPERIAN', 'TRANSUNION'];

export default function DisputeAccountsPageNew({ 
  clientId, 
  clientData, 
  signatureData, 
  onComplete, 
  onAccountsChange,
  onLettersGenerated 
}) {
  // Account state
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Per-account selections
  const [accountFraudCategories, setAccountFraudCategories] = useState({}); // { accountId: 'CREDIT_CARD' }
  const [accountBureauSelections, setAccountBureauSelections] = useState({}); // { accountId: ['EQUIFAX', 'EXPERIAN'] }
  
  // Letter type selections
  const [selectedLetterTypes, setSelectedLetterTypes] = useState(['REGULAR']);
  const [accountLetterAssignments, setAccountLetterAssignments] = useState({}); // { accountId: ['REGULAR', 'STUDENT_LOAN'] }
  
  // Generation state
  const [generatingLetters, setGeneratingLetters] = useState({});
  const [generatedLetters, setGeneratedLetters] = useState({});
  
  // UI state
  const [expandedAccounts, setExpandedAccounts] = useState({});
  const [showLetterTypeSelector, setShowLetterTypeSelector] = useState(false);
  
  // Quick Paste Parser state
  const [showQuickParser, setShowQuickParser] = useState(false);
  const [pastedReportText, setPastedReportText] = useState('');
  const [parsingReport, setParsingReport] = useState(false);
  const [parsedAccounts, setParsedAccounts] = useState([]);
  const [showParsedResults, setShowParsedResults] = useState(false);
  
  // Template selection state (NEW - E-Oscar variation)
  const [selectedTemplate, setSelectedTemplate] = useState(1);
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);
  const [templatePreviewContent, setTemplatePreviewContent] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Fetch accounts on mount
  useEffect(() => {
    fetchAccounts();
  }, [clientId]);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/dispute-accounts/${clientId}`);
      const fetchedAccounts = response.data.accounts || [];
      setAccounts(fetchedAccounts);
      
      // Initialize default selections
      const defaultCategories = {};
      const defaultBureaus = {};
      const defaultLetterAssignments = {};
      
      fetchedAccounts.forEach(acc => {
        defaultCategories[acc.id] = guessCategory(acc);
        defaultBureaus[acc.id] = acc.bureaus || ['EQUIFAX', 'EXPERIAN', 'TRANSUNION'];
        defaultLetterAssignments[acc.id] = ['REGULAR'];
      });
      
      setAccountFraudCategories(defaultCategories);
      setAccountBureauSelections(defaultBureaus);
      setAccountLetterAssignments(defaultLetterAssignments);
      
      if (onAccountsChange) {
        onAccountsChange(fetchedAccounts);
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
      toast.error('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  // Guess category based on creditor name
  const guessCategory = (account) => {
    const name = (account.creditor_name || account.creditor || '').toLowerCase();
    if (name.includes('card') || name.includes('visa') || name.includes('mastercard') || name.includes('amex') || name.includes('discover') || name.includes('capital one')) return 'CREDIT_CARD';
    if (name.includes('auto') || name.includes('car') || name.includes('motor') || name.includes('vehicle') || name.includes('toyota') || name.includes('ford') || name.includes('honda')) return 'AUTO';
    if (name.includes('phone') || name.includes('mobile') || name.includes('wireless') || name.includes('verizon') || name.includes('at&t') || name.includes('t-mobile') || name.includes('sprint')) return 'MOBILE';
    if (name.includes('electric') || name.includes('gas') || name.includes('water') || name.includes('utility') || name.includes('power') || name.includes('energy')) return 'UTILITY';
    if (name.includes('rent') || name.includes('apartment') || name.includes('property')) return 'RENTAL';
    if (name.includes('medical') || name.includes('hospital') || name.includes('health') || name.includes('clinic') || name.includes('doctor')) return 'MEDICAL';
    if (name.includes('student') || name.includes('education') || name.includes('navient') || name.includes('nelnet') || name.includes('sallie') || name.includes('dept of ed')) return 'STUDENT_LOAN';
    if (name.includes('mortgage') || name.includes('home') || name.includes('real estate')) return 'REAL_ESTATE';
    if (name.includes('insurance') || name.includes('geico') || name.includes('state farm') || name.includes('allstate')) return 'INSURANCE';
    if (name.includes('loan') || name.includes('finance') || name.includes('lending')) return 'LOAN_LEASE';
    return 'OTHER';
  };

  // Toggle fraud category for an account
  const setAccountCategory = (accountId, category) => {
    setAccountFraudCategories(prev => ({ ...prev, [accountId]: category }));
  };

  // Toggle bureau for an account
  const toggleBureau = (accountId, bureau) => {
    setAccountBureauSelections(prev => {
      const current = prev[accountId] || [];
      if (current.includes(bureau)) {
        return { ...prev, [accountId]: current.filter(b => b !== bureau) };
      } else {
        return { ...prev, [accountId]: [...current, bureau] };
      }
    });
  };

  // Toggle letter type assignment for an account
  const toggleLetterAssignment = (accountId, letterType) => {
    setAccountLetterAssignments(prev => {
      const current = prev[accountId] || [];
      if (current.includes(letterType)) {
        return { ...prev, [accountId]: current.filter(lt => lt !== letterType) };
      } else {
        return { ...prev, [accountId]: [...current, letterType] };
      }
    });
  };

  // Toggle letter type selection
  const toggleLetterType = (letterType) => {
    setSelectedLetterTypes(prev => {
      if (prev.includes(letterType)) {
        return prev.filter(lt => lt !== letterType);
      } else {
        return [...prev, letterType];
      }
    });
  };
  
  // Preview letter template
  const previewTemplate = async (templateId) => {
    setLoadingPreview(true);
    try {
      const response = await axios.post(`${API}/api/generate-letter-preview`, {
        template_id: templateId,
        bureau: 'experian',
        client_id: clientId
      });
      
      if (response.data.success) {
        setTemplatePreviewContent(response.data.letter_content);
        setShowTemplatePreview(true);
      } else {
        toast.error('Failed to load template preview');
      }
    } catch (error) {
      console.error('Error loading preview:', error);
      toast.error('Failed to load template preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  // Generate categorized dispute letter
  const generateCategorizedLetter = async (bureau, letterType) => {
    const key = `${bureau}-${letterType}`;
    setGeneratingLetters(prev => ({ ...prev, [key]: true }));
    
    try {
      // Build accounts_by_category for this bureau and letter type
      const accountsByCategory = {};
      
      accounts.forEach(acc => {
        const accBureaus = accountBureauSelections[acc.id] || [];
        const accLetterTypes = accountLetterAssignments[acc.id] || ['REGULAR'];
        const category = accountFraudCategories[acc.id] || 'OTHER';
        
        // Include account if it's assigned to this bureau AND this letter type
        if (accBureaus.includes(bureau) && accLetterTypes.includes(letterType)) {
          if (!accountsByCategory[category]) {
            accountsByCategory[category] = [];
          }
          accountsByCategory[category].push(acc.id);
        }
      });
      
      const response = await axios.post(
        `${API}/api/dispute/categorized-letter`,
        {
          client_id: clientId,
          bureau: bureau,
          accounts_by_category: accountsByCategory,
          letter_type: letterType,
          signature_data: signatureData,
          template_id: selectedTemplate  // NEW: Include selected template
        },
        { responseType: 'blob' }
      );
      
      // Download the PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${clientData?.name || 'Client'}_${bureau}_${letterType}_Dispute.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setGeneratedLetters(prev => ({ ...prev, [key]: true }));
      toast.success(`${bureau} ${LETTER_TYPES.find(lt => lt.value === letterType)?.label || letterType} downloaded!`);
      
      if (onLettersGenerated) {
        onLettersGenerated();
      }
    } catch (error) {
      console.error('Error generating letter:', error);
      toast.error(`Failed to generate ${bureau} letter`);
    } finally {
      setGeneratingLetters(prev => ({ ...prev, [key]: false }));
    }
  };

  // Generate all selected letters for all bureaus
  const generateAllLetters = async () => {
    for (const bureau of BUREAUS) {
      for (const letterType of selectedLetterTypes) {
        await generateCategorizedLetter(bureau, letterType);
      }
    }
  };

  // Get count of accounts by category
  const getAccountCountByCategory = (category) => {
    return accounts.filter(acc => accountFraudCategories[acc.id] === category).length;
  };

  // Get accounts with at least one bureau selected
  const getSelectedAccounts = () => {
    return accounts.filter(acc => (accountBureauSelections[acc.id] || []).length > 0);
  };

  // Parse pasted credit report text
  const parseReportText = async () => {
    if (!pastedReportText || pastedReportText.trim().length < 20) {
      toast.error('Please paste credit report text (at least 20 characters)');
      return;
    }
    
    setParsingReport(true);
    try {
      const response = await axios.post(`${API}/api/credit-report/parse-accounts`, {
        text: pastedReportText,
        client_id: clientId
      });
      
      if (response.data.success) {
        setParsedAccounts(response.data.accounts || []);
        setShowParsedResults(true);
        
        const complete = response.data.complete_accounts || 0;
        const incomplete = response.data.incomplete_accounts || 0;
        
        toast.success(`Found ${complete} accounts${incomplete > 0 ? ` (${incomplete} incomplete)` : ''}`);
        
        // Refresh accounts list to show newly added accounts
        if (complete > 0) {
          await fetchAccounts();
        }
      } else {
        toast.error(response.data.error || 'Failed to parse accounts');
      }
    } catch (error) {
      console.error('Error parsing report:', error);
      toast.error('Failed to parse credit report');
    } finally {
      setParsingReport(false);
    }
  };

  // Copy parsed results to clipboard
  const copyParsedResults = () => {
    const lines = parsedAccounts
      .filter(a => !a.incomplete)
      .map(a => a.formatted)
      .join('\n');
    
    navigator.clipboard.writeText(lines);
    toast.success('Copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-zinc-900 border-zinc-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-xl text-white flex items-center gap-2">
            <Shield className="w-6 h-6 text-amber-500" />
            Dispute Accounts - Select Categories & Letter Types
          </CardTitle>
          <p className="text-sm text-zinc-400">
            Assign each account to a fraud category and select which dispute letters to include in your package.
          </p>
        </CardHeader>
      </Card>

      {/* Quick Paste Parser - Expandable Section */}
      <Card className="bg-zinc-900 border-zinc-700">
        <CardHeader 
          className="pb-2 cursor-pointer hover:bg-zinc-800/50 transition-colors rounded-t-lg"
          onClick={() => setShowQuickParser(!showQuickParser)}
        >
          <CardTitle className="text-lg text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ClipboardPaste className="w-5 h-5 text-blue-400" />
              Quick Paste Parser
              <span className="text-xs bg-blue-600/20 text-blue-400 px-2 py-0.5 rounded ml-2">
                Credit Karma / Experian
              </span>
            </div>
            {showQuickParser ? (
              <ChevronUp className="w-5 h-5 text-zinc-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-zinc-400" />
            )}
          </CardTitle>
          <p className="text-xs text-zinc-400">
            Paste raw credit report text to extract accounts as: ACCOUNT NAME-OPEN DATE-$BALANCE
          </p>
        </CardHeader>
        
        {showQuickParser && (
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm text-zinc-400 mb-2 block">
                Paste your credit report text below (Credit Karma, Experian, or any format):
              </label>
              <Textarea
                value={pastedReportText}
                onChange={(e) => setPastedReportText(e.target.value)}
                placeholder={`Paste credit report text here...

Example format:
CAPITAL ONE
Reported: Mar. 17, 2026
$1,712.00
Closed

Opened
May 24, 2018

Balance
$1712

NCB MANAGEMENT SERVICES
Collection
Opened: Jun 24, 2025
Balance: $1,875`}
                className="min-h-[200px] bg-zinc-800 border-zinc-700 text-white font-mono text-sm"
                data-testid="quick-paste-textarea"
              />
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={parseReportText}
                disabled={parsingReport || !pastedReportText.trim()}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="parse-accounts-btn"
              >
                {parsingReport ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Parsing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Parse Accounts
                  </>
                )}
              </Button>
              
              <Button
                onClick={() => {
                  setPastedReportText('');
                  setParsedAccounts([]);
                  setShowParsedResults(false);
                }}
                variant="outline"
                className="border-zinc-700 text-zinc-300"
              >
                Clear
              </Button>
            </div>
            
            {/* Parsed Results */}
            {showParsedResults && parsedAccounts.length > 0 && (
              <div className="mt-4 p-4 bg-zinc-800 rounded-lg border border-zinc-700">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-white flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    Extracted Accounts ({parsedAccounts.filter(a => !a.incomplete).length})
                  </h4>
                  <Button
                    onClick={copyParsedResults}
                    size="sm"
                    variant="outline"
                    className="border-zinc-600 text-zinc-300 text-xs"
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    Copy All
                  </Button>
                </div>
                
                <div className="space-y-1 font-mono text-sm max-h-[300px] overflow-y-auto">
                  {parsedAccounts.map((acc, idx) => (
                    <div 
                      key={idx}
                      className={`px-2 py-1 rounded ${
                        acc.incomplete 
                          ? 'bg-yellow-900/20 text-yellow-400' 
                          : 'bg-green-900/20 text-green-400'
                      }`}
                    >
                      {acc.formatted}
                      {acc.confidence && (
                        <span className="text-xs text-zinc-500 ml-2">
                          ({Math.round(acc.confidence * 100)}%)
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                
                {parsedAccounts.some(a => a.incomplete) && (
                  <p className="text-xs text-yellow-400 mt-2">
                    * Yellow items are missing the open date and were not added to your accounts.
                  </p>
                )}
                
                <p className="text-xs text-zinc-500 mt-2">
                  Complete accounts have been automatically added to your dispute list below.
                </p>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Accounts List */}
      <Card className="bg-zinc-900 border-zinc-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-white">
            Your Accounts ({accounts.length})
          </CardTitle>
          <p className="text-xs text-zinc-400">
            Select fraud category for each account and which bureaus to dispute
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {accounts.length === 0 ? (
            <p className="text-zinc-500 text-center py-8">No accounts found. Go back to import your credit report.</p>
          ) : (
            accounts.map((account, idx) => {
              const category = accountFraudCategories[account.id] || 'OTHER';
              const bureaus = accountBureauSelections[account.id] || [];
              const letterTypes = accountLetterAssignments[account.id] || ['REGULAR'];
              const CategoryIcon = FRAUD_CATEGORIES.find(c => c.value === category)?.icon || HelpCircle;
              
              return (
                <div 
                  key={account.id || idx}
                  className="bg-zinc-800 border border-zinc-700 rounded-lg p-4"
                >
                  {/* Account Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-600/20 rounded-lg flex items-center justify-center">
                        <CategoryIcon className="w-5 h-5 text-amber-500" />
                      </div>
                      <div>
                        <h3 className="font-bold text-white">{account.creditor_name || account.creditor || 'Unknown Creditor'}</h3>
                        <p className="text-xs text-zinc-400">
                          {account.account_number ? `#${account.account_number.slice(-4)}` : ''} 
                          {account.balance ? ` • Balance: $${parseInt(account.balance).toLocaleString()}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`text-xs px-2 py-1 rounded ${category ? 'bg-amber-600/20 text-amber-400' : 'bg-zinc-700 text-zinc-400'}`}>
                        {FRAUD_CATEGORIES.find(c => c.value === category)?.label || 'Select Category'}
                      </span>
                    </div>
                  </div>

                  {/* Category Selection */}
                  <div className="mb-3">
                    <label className="text-xs text-zinc-500 mb-1 block">Fraud Category:</label>
                    <div className="flex flex-wrap gap-1">
                      {FRAUD_CATEGORIES.map(cat => (
                        <button
                          key={cat.value}
                          onClick={() => setAccountCategory(account.id, cat.value)}
                          className={`px-2 py-1 text-xs rounded transition-colors ${
                            category === cat.value
                              ? 'bg-amber-600 text-white'
                              : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'
                          }`}
                        >
                          {cat.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Bureau Selection */}
                  <div className="mb-3">
                    <label className="text-xs text-zinc-500 mb-1 block">Dispute to Bureaus:</label>
                    <div className="flex gap-2">
                      {BUREAUS.map(bureau => (
                        <button
                          key={bureau}
                          onClick={() => toggleBureau(account.id, bureau)}
                          className={`px-3 py-1.5 text-xs rounded font-medium transition-colors ${
                            bureaus.includes(bureau)
                              ? 'bg-green-600 text-white'
                              : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'
                          }`}
                        >
                          {bureaus.includes(bureau) && <Check className="w-3 h-3 inline mr-1" />}
                          {bureau}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Letter Type Assignment */}
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">Include in Letter Types:</label>
                    <div className="flex flex-wrap gap-1">
                      {LETTER_TYPES.map(lt => (
                        <button
                          key={lt.value}
                          onClick={() => toggleLetterAssignment(account.id, lt.value)}
                          className={`px-2 py-1 text-xs rounded transition-colors ${
                            letterTypes.includes(lt.value)
                              ? 'bg-blue-600 text-white'
                              : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'
                          }`}
                        >
                          {letterTypes.includes(lt.value) && <Check className="w-3 h-3 inline mr-1" />}
                          {lt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* E-Oscar Template Selection - NEW */}
      <Card className="bg-zinc-900 border-zinc-700 border-amber-500/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            Letter Template Style (E-Oscar Variation)
          </CardTitle>
          <p className="text-xs text-zinc-400">
            Use different templates for each round of disputes to avoid credit bureau pattern detection.
            Templates 4-5 include Metro 2 compliance language for technical reporting errors.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {LETTER_TEMPLATES.map(template => {
              const isSelected = selectedTemplate === template.id;
              const colorClasses = {
                red: 'border-red-500 bg-red-500/10',
                blue: 'border-blue-500 bg-blue-500/10',
                purple: 'border-purple-500 bg-purple-500/10',
                orange: 'border-orange-500 bg-orange-500/10',
                amber: 'border-amber-500 bg-amber-500/10',
                green: 'border-green-500 bg-green-500/10'
              };
              const iconColors = {
                red: 'text-red-500',
                blue: 'text-blue-500',
                purple: 'text-purple-500',
                orange: 'text-orange-500',
                amber: 'text-amber-500',
                green: 'text-green-500'
              };
              
              return (
                <button
                  key={template.id}
                  onClick={() => setSelectedTemplate(template.id)}
                  className={`p-4 rounded-lg border-2 text-left transition-all relative ${
                    isSelected
                      ? colorClasses[template.color]
                      : 'border-zinc-700 bg-zinc-800 hover:border-zinc-600'
                  }`}
                  data-testid={`template-${template.id}`}
                >
                  {isSelected && (
                    <div className="absolute top-2 right-2">
                      <Check className={`w-5 h-5 ${iconColors[template.color]}`} />
                    </div>
                  )}
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-lg font-bold ${isSelected ? iconColors[template.color] : 'text-zinc-500'}`}>
                      #{template.id}
                    </span>
                    {template.category === 'identity_theft_metro2' && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-orange-600/30 text-orange-400 rounded">
                        ID THEFT + METRO 2
                      </span>
                    )}
                  </div>
                  <h4 className={`font-medium text-sm ${isSelected ? 'text-white' : 'text-zinc-300'}`}>
                    {template.name}
                  </h4>
                  <p className="text-xs text-zinc-500 mt-1">{template.description}</p>
                  <p className={`text-[10px] mt-2 ${isSelected ? iconColors[template.color] : 'text-zinc-600'}`}>
                    Best for: {template.recommended}
                  </p>
                </button>
              );
            })}
          </div>
          
          {/* Preview Button */}
          <div className="mt-4 flex items-center justify-between">
            <Button
              onClick={() => previewTemplate(selectedTemplate)}
              disabled={loadingPreview}
              variant="outline"
              className="border-zinc-600 text-zinc-300 hover:bg-zinc-800"
              size="sm"
            >
              {loadingPreview ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading Preview...</>
              ) : (
                <><Eye className="w-4 h-4 mr-2" /> Preview Template #{selectedTemplate}</>
              )}
            </Button>
            <p className="text-xs text-amber-400">
              Selected: <span className="font-medium">{LETTER_TEMPLATES.find(t => t.id === selectedTemplate)?.name}</span>
            </p>
          </div>
        </CardContent>
      </Card>
      
      {/* Template Preview Modal */}
      {showTemplatePreview && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-zinc-700">
              <h3 className="text-lg font-bold text-white">
                Template Preview: {LETTER_TEMPLATES.find(t => t.id === selectedTemplate)?.name}
              </h3>
              <button
                onClick={() => setShowTemplatePreview(false)}
                className="text-zinc-400 hover:text-white p-2"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <pre className="whitespace-pre-wrap font-mono text-sm text-zinc-300 bg-zinc-800 p-4 rounded-lg">
                {templatePreviewContent}
              </pre>
            </div>
            <div className="p-4 border-t border-zinc-700 flex justify-end gap-3">
              <Button
                onClick={() => setShowTemplatePreview(false)}
                variant="outline"
                className="border-zinc-600"
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(templatePreviewContent);
                  toast.success('Template copied to clipboard');
                }}
                className="bg-amber-600 hover:bg-amber-700"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Template
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Letter Type Selection */}
      <Card className="bg-zinc-900 border-zinc-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-white">
            Select Letter Types to Generate
          </CardTitle>
          <p className="text-xs text-zinc-400">
            Choose which dispute letters to include in your package
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {LETTER_TYPES.map(lt => {
              const Icon = lt.icon;
              const isSelected = selectedLetterTypes.includes(lt.value);
              
              return (
                <button
                  key={lt.value}
                  onClick={() => toggleLetterType(lt.value)}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    isSelected
                      ? 'border-amber-500 bg-amber-500/10'
                      : 'border-zinc-700 bg-zinc-800 hover:border-zinc-600'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    {isSelected && <Check className="w-4 h-4 text-amber-500" />}
                    <Icon className={`w-5 h-5 ${isSelected ? 'text-amber-500' : 'text-zinc-500'}`} />
                  </div>
                  <h4 className={`font-medium ${isSelected ? 'text-white' : 'text-zinc-300'}`}>{lt.label}</h4>
                  <p className="text-xs text-zinc-500 mt-1">{lt.description}</p>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Smart Document Recommendations */}
      <Card className="bg-zinc-900 border-zinc-700 border-green-500/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-green-500" />
            Recommended Documents for This Client
          </CardTitle>
          <p className="text-xs text-zinc-400">
            Based on account types selected, here's what you should include in the dispute package
          </p>
        </CardHeader>
        <CardContent>
          {(() => {
            const recommendations = getDocumentRecommendations(accountFraudCategories, selectedLetterTypes);
            return (
              <div className="space-y-4">
                {/* Required Documents */}
                <div>
                  <h4 className="text-sm font-bold text-red-400 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> REQUIRED DOCUMENTS
                  </h4>
                  <div className="space-y-2">
                    {recommendations.required.map((doc, idx) => (
                      <div key={idx} className="flex items-start gap-3 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                        <Check className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-white">{doc.name}</p>
                          <p className="text-xs text-zinc-400">{doc.reason}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recommended Based on Account Types */}
                {recommendations.recommended.length > 0 && (
                  <div>
                    <h4 className="text-sm font-bold text-amber-400 mb-2 flex items-center gap-2">
                      <Sparkles className="w-4 h-4" /> RECOMMENDED FOR YOUR ACCOUNTS
                    </h4>
                    <div className="space-y-2">
                      {recommendations.recommended.map((doc, idx) => (
                        <div key={idx} className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                          <Check className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-white">{doc.name}</p>
                            <p className="text-xs text-zinc-400">{doc.reason}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Optional / Extra Strength */}
                <div>
                  <h4 className="text-sm font-bold text-blue-400 mb-2 flex items-center gap-2">
                    <Shield className="w-4 h-4" /> OPTIONAL (EXTRA STRENGTH)
                  </h4>
                  <div className="space-y-2">
                    {recommendations.optional.map((doc, idx) => (
                      <div key={idx} className="flex items-start gap-3 bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                        <FileText className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-white">{doc.name}</p>
                          <p className="text-xs text-zinc-400">{doc.reason}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Lob.com Mailing Reminder */}
                <div className="mt-4 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                  <p className="text-sm text-purple-300 flex items-center gap-2">
                    <span className="text-lg">📬</span>
                    <span><strong>Ready for Lob.com Mailing:</strong> All documents will be formatted for certified mail delivery</span>
                  </p>
                </div>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Generate Letters */}
      <Card className="bg-zinc-900 border-zinc-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-white">
            Generate Dispute Letters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            {BUREAUS.map(bureau => (
              <div key={bureau} className="space-y-2">
                <h4 className="font-bold text-white text-center">{bureau}</h4>
                {selectedLetterTypes.map(letterType => {
                  const key = `${bureau}-${letterType}`;
                  const isGenerating = generatingLetters[key];
                  const isGenerated = generatedLetters[key];
                  const ltInfo = LETTER_TYPES.find(lt => lt.value === letterType);
                  
                  return (
                    <Button
                      key={key}
                      onClick={() => generateCategorizedLetter(bureau, letterType)}
                      disabled={isGenerating}
                      className={`w-full text-xs ${
                        isGenerated
                          ? 'bg-green-600 hover:bg-green-700'
                          : 'bg-amber-600 hover:bg-amber-700'
                      }`}
                      size="sm"
                    >
                      {isGenerating ? (
                        <span className="animate-pulse">Generating...</span>
                      ) : isGenerated ? (
                        <>
                          <Check className="w-3 h-3 mr-1" />
                          {ltInfo?.label?.replace(' Letter', '') || letterType}
                        </>
                      ) : (
                        <>
                          <Download className="w-3 h-3 mr-1" />
                          {ltInfo?.label?.replace(' Letter', '') || letterType}
                        </>
                      )}
                    </Button>
                  );
                })}
              </div>
            ))}
          </div>
          
          {/* Generate All Button */}
          <div className="mt-6 text-center">
            <Button
              onClick={generateAllLetters}
              className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800"
              size="lg"
            >
              <Download className="w-5 h-5 mr-2" />
              Generate All Selected Letters ({selectedLetterTypes.length * 3} total)
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Continue Button */}
      <div className="flex justify-end">
        <Button
          onClick={onComplete}
          disabled={getSelectedAccounts().length === 0}
          className="bg-green-600 hover:bg-green-700"
          size="lg"
        >
          Continue to Supplemental Letters
          <ChevronRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    </div>
  );
}
